// Booking classification — mirrors Flutter `Appointment` model logic.

export function bookingId(b) {
  return b?.bookingIds || b?.bookingId || b?.id || '';
}

export function isEmergencyBooking(b) {
  if (!b) return false;
  const type = (b.type || '').toLowerCase();
  const slot = (b.slot || '').toUpperCase();
  const docId = (b.doctorId || '').toLowerCase();
  return (
    type === 'emergency' || type === 'sos' ||
    slot === 'EMERGENCY' || slot === 'SOS' ||
    docId === 'emergency' || docId === 'sos'
  );
}

export function isSosBooking(b) {
  if (!b) return false;
  const type = (b.type || '').toLowerCase();
  const slot = (b.slot || '').toUpperCase();
  const docId = (b.doctorId || '').toLowerCase();
  return type === 'sos' || docId === 'sos' || slot === 'SOS';
}

export function parseBookingDate(b) {
  const raw = b?.date || b?.bookingDate || b?.createdAt;
  if (!raw) return null;
  try {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

export function isUpcomingBooking(b) {
  const st = (b?.status || '').toLowerCase();
  if (st === 'cancelled' || st === 'checked_in') return false;
  if (isEmergencyBooking(b)) return st !== 'cancelled';

  const date = parseBookingDate(b);
  if (!date) return st !== 'cancelled';

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const apptDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (apptDay > today) return true;
  if (apptDay < today) return false;

  const slot = b?.slot;
  if (!slot || slot === 'Walk-in' || slot === 'EMERGENCY' || slot === 'SOS') return true;

  try {
    const [h, m] = String(slot).split(':').map(Number);
    const slotDt = new Date(date.getFullYear(), date.getMonth(), date.getDate(), h, m || 0);
    return slotDt.getTime() + 30 * 60 * 1000 > now.getTime();
  } catch {
    return true;
  }
}

export function categorizeBookings(bookings) {
  const sorted = [...(bookings || [])].sort((a, b) => {
    const da = parseBookingDate(a)?.getTime() || 0;
    const db = parseBookingDate(b)?.getTime() || 0;
    return db - da;
  });

  const emergencies = sorted.filter(b => {
    const st = (b.status || '').toLowerCase();
    return isEmergencyBooking(b) && st !== 'cancelled';
  });

  const upcoming = sorted
    .filter(b => isUpcomingBooking(b) && !isEmergencyBooking(b))
    .sort((a, b) => (parseBookingDate(a)?.getTime() || 0) - (parseBookingDate(b)?.getTime() || 0));

  const past = sorted
    .filter(b => !isUpcomingBooking(b) && !isEmergencyBooking(b))
    .sort((a, b) => (parseBookingDate(b)?.getTime() || 0) - (parseBookingDate(a)?.getTime() || 0));

  return { emergencies, upcoming, past, all: sorted };
}

export function fmtTime(t) {
  if (!t || t === 'Walk-in' || t === 'EMERGENCY' || t === 'SOS') return t || 'Walk-in';
  const [h, m] = String(t).split(':').map(Number);
  if (Number.isNaN(h)) return t;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${h12}:${String(m || 0).padStart(2, '0')} ${ampm}`;
}

export function fmtDate(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return String(d);
  }
}

export function fmtDateBlock(d) {
  const dt = d instanceof Date ? d : parseBookingDate({ date: d });
  if (!dt) return { day: '--', month: '---', year: '----' };
  const months = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return {
    day: String(dt.getDate()).padStart(2, '0'),
    month: months[dt.getMonth() + 1] || '',
    year: String(dt.getFullYear()),
  };
}

export function statusMeta(status) {
  const st = (status || 'pending').toLowerCase();
  const map = {
    confirmed:  { label: 'Confirmed', color: 'var(--success)',  bg: 'rgba(34,197,94,0.1)' },
    pending:    { label: 'Scheduled', color: 'var(--info, #3b82f6)', bg: 'rgba(59,130,246,0.1)' },
    checked_in: { label: 'Checked In', color: '#00b894', bg: 'rgba(0,184,148,0.1)' },
    cancelled:  { label: 'Cancelled', color: 'var(--danger)', bg: 'rgba(239,68,68,0.1)' },
  };
  return map[st] || map.pending;
}

export function bookingOtp(b) {
  if (!b) return '';
  const raw = b.bookingOtp ?? b.otp ?? b.checkInOtp ?? b.check_in_otp ?? '';
  if (raw === null || raw === undefined) return '';
  const s = String(raw).trim();
  return s && s !== 'undefined' && s !== 'null' ? s : '';
}

/** True when a booking should show the patient-facing check-in OTP (matches Flutter app). */
export function shouldShowCheckInOtp(b, { onDetail = false } = {}) {
  const otp = bookingOtp(b);
  if (!otp) return false;
  const st = (b?.status || '').toLowerCase();
  if (st === 'checked_in') return false;
  // List view hides OTP for cancelled; detail screen in the app still shows it.
  if (!onDetail && st === 'cancelled') return false;
  return true;
}

export function isDoctorBooking(b) {
  return b?.bookingType === 'doctor' || (b?.doctorId && b.doctorId !== 'general_op' && b.doctorId !== 'walkin');
}

export function displayDoctorName(b) {
  if (isEmergencyBooking(b)) {
    return b?.specialization || b?.emergencyType || 'Emergency';
  }
  if (isDoctorBooking(b)) {
    const n = b?.doctorName || 'Doctor';
    return n.startsWith('Dr.') ? n : `Dr. ${n}`;
  }
  return b?.doctorName || 'Walk-in OP';
}

export function displayHospitalName(b) {
  const n = (b?.hospitalName || '').trim();
  if (n && !/searching nearby/i.test(n)) return n;
  if (isSosBooking(b)) return 'Searching nearby hospitals…';
  return n || 'HCEN Hospital';
}
